import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  DropdownAction,
  ModalDefault,
  NoResults,
  Notice,
  Row,
  SelectorPageStatus,
  StatePill,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, formatHours } from "@components/ow";
import { WEEKDAY_LABELS, fullDate, weekdayOf, todayDateInput } from "@helpers/timeClock";
import DayOffModal from "./DayOffModal";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

const Inline = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const currentYear = () => new Date().getFullYear();

/**
 * Aba "Folgas da empresa" do ponto (só ADM): dias que o sistema não conhece
 * (feriados estaduais e municipais, pontes, aniversário da empresa), que zeram ou
 * reduzem a jornada esperada de todos os contratos CLT.
 */
export default function CompanyDayOffs() {
  const dayOffService = useService("companyDayOffs");
  const today = todayDateInput();
  const years = [currentYear() - 1, currentYear(), currentYear() + 1].map((y) => ({ value: String(y), label: String(y) }));

  const [year, setYear] = useState(years[1]);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  // undefined: fechado; null: nova folga; objeto: editar
  const [editing, setEditing] = useState(undefined);
  const [toRemove, setToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await dayOffService.listByYear(year.value);
      setList(resp?.data?.list || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao carregar as folgas da empresa");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year.value]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  async function handleRemove() {
    setRemoving(true);
    try {
      await dayOffService.removeDayOff(toRemove.id);
      toast.success("Folga excluída");
      setToRemove(null);
      loadList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao excluir a folga");
    }
    setRemoving(false);
  }

  // As que já passaram ficam em N400, no lugar da linha cinza de antes
  const muted = (dayOff) => (dayOff.occurrence_date < today ? "N400" : "N900");

  const columns = [
    {
      id: "date",
      header: "Data",
      size: 150,
      cell: ({ row }) => (
        <Inline>
          <Text $text={fullDate(row.original.occurrence_date)} $variant="paragraph_small_medium" $color={muted(row.original)} />
          <Text $text={WEEKDAY_LABELS[weekdayOf(row.original.occurrence_date)]} $variant="caption_medium" $color="N400" />
        </Inline>
      ),
    },
    {
      id: "description",
      header: "Descrição",
      size: 320,
      cell: ({ row }) => (
        <Text $text={escapeHtml(row.original.description)} $variant="paragraph_small_medium" $color={muted(row.original)} />
      ),
    },
    {
      id: "hours",
      header: "Folga",
      size: 130,
      cell: ({ row }) =>
        row.original.hours_off ? (
          <StatePill $text={formatHours(row.original.hours_off * 60)} $variant="orange" $size="small" />
        ) : (
          <StatePill $text="Dia inteiro" $variant="gray" $size="small" />
        ),
    },
    {
      id: "repeats",
      header: "Recorrência",
      size: 140,
      cell: ({ row }) =>
        row.original.repeats_yearly ? (
          <StatePill $text="Todo ano" $variant="blue" $size="small" />
        ) : (
          <Text $text="Data única" $variant="paragraph_small_medium" $color="N500" />
        ),
    },
    {
      id: "actions",
      header: "",
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => {
        const dayOff = row.original;
        if (!dayOff.can_edit && !dayOff.can_delete) {
          return <Text $text="Já aplicada" $variant="caption_medium" $color="N400" />;
        }
        return (
          <DropdownAction
            options={[
              { label: "Editar", action: () => setEditing(dayOff), show: !!dayOff.can_edit },
              { label: "Excluir", action: () => setToRemove(dayOff), isImportant: true, show: !!dayOff.can_delete },
            ]}
          />
        );
      },
    },
  ];

  return (
    <Stack>
      <Row $justifyContent="space-between" $alignItems="center" $gap={16}>
        <SelectorPageStatus options={years} selected={year} onChange={setYear} />
        <div>
          <Button $text="Nova folga" $variant="outline" $noWidth $action={() => setEditing(null)} />
        </div>
      </Row>

      <Text
        $text="Feriados estaduais e municipais, pontes e datas próprias da empresa. Valem para todos os colaboradores CLT ativos; os feriados nacionais já entram sozinhos."
        $variant="paragraph_small_medium"
        $color="N500"
      />

      <BasicTable
        data={list}
        columns={columns}
        isLoading={loading}
        getRowId={(dayOff) => `${dayOff.id}-${dayOff.occurrence_date}`}
        noResults={
          <NoResults
            notResultText={`Nenhuma folga cadastrada em ${year.label}`}
            includeText="Nova folga"
            includeAction={() => setEditing(null)}
          />
        }
      />

      {editing !== undefined ? (
        <DayOffModal
          dayOff={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            loadList();
          }}
        />
      ) : null}

      {toRemove ? (
        <ModalDefault
          $showModal
          $setShowModal={() => !removing && setToRemove(null)}
          $noClose={removing}
          $size="medium"
          $title="Excluir folga?"
          $contentBackground="N0"
          $footer={
            <>
              <Button $text="Cancelar" $variant="outline" $noWidth $isDisabled={removing} $action={() => setToRemove(null)} />
              <Button $text="Excluir folga" $variant="danger" $noWidth $isLoading={removing} $action={handleRemove} />
            </>
          }>
          <Stack>
            <Text
              $text="A folga <strong>%s</strong> de <strong>%s</strong> deixa de valer."
              $replaces={[escapeHtml(toRemove.description), fullDate(toRemove.occurrence_date)]}
              $variant="paragraph_small_medium"
              $color="N700"
              $colorEmphasis="N900"
            />
            {toRemove.repeats_yearly ? (
              <Notice
                $variant="gray"
                $text="Esta folga se repete todo ano. As que já passaram continuam valendo; só as próximas voltam a ser dias úteis."
              />
            ) : null}
          </Stack>
        </ModalDefault>
      ) : null}
    </Stack>
  );
}
